import { Icon, XIcon } from '@phosphor-icons/react';
import { useState, ReactNode, useEffect } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { ActionBarInteractionTrigger } from './ActionBarShell';
import { ActionVariant } from './ActionBar.types';
import { usePopoverCloseSignal } from '@/stores';
import { cn } from '@/lib/utils';
import { useActionBarResponsiveClasses } from './useActionBarResponsiveClasses';

interface ActionBarMenuProps {
    icon: Icon;
    label: string;
    trigger?: ActionBarInteractionTrigger;
    title?: string;
    active?: boolean;
    onClear?: () => void;
    variant?: ActionVariant.DEFAULT | ActionVariant.PRIMARY;
    align?: 'start' | 'center' | 'end';
    children: ReactNode;
    disabled?: boolean;
    triggerTestId?: string;
}

export function ActionBarMenu({
    icon: IconComponent,
    label,
    trigger = ActionBarInteractionTrigger.CLICK,
    title,
    active = false,
    onClear,
    variant = ActionVariant.DEFAULT,
    align = 'start',
    children,
    disabled = false,
    triggerTestId,
}: ActionBarMenuProps) {
    const responsive = useActionBarResponsiveClasses();
    const isPrimary = variant === ActionVariant.PRIMARY;
    const [isOpen, setIsOpen] = useState(false);
    const closeSignal = usePopoverCloseSignal(
        (state: { signal: number; trigger: () => void }) => state.signal,
    );

    useEffect(() => {
        if (closeSignal > 0) {
            setIsOpen(false);
        }
    }, [closeSignal]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    const handleTriggerInteraction =
        trigger === ActionBarInteractionTrigger.HOVER
            ? {
                  onPointerEnter: () => setIsOpen(true),
                  onPointerLeave: () => setIsOpen(false),
              }
            : {};

    const handleContentInteraction =
        trigger === ActionBarInteractionTrigger.HOVER
            ? { onPointerLeave: () => setIsOpen(false) }
            : {};

    const handleClearClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onClear?.();
    };

    return (
        <DropdownMenu
            open={disabled ? false : isOpen}
            onOpenChange={(next) => {
                if (disabled) {
                    return;
                }
                setIsOpen(next);
            }}
            modal={false}
        >
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid={triggerTestId}
                    {...handleTriggerInteraction}
                    aria-pressed={active}
                    aria-label={label}
                    disabled={disabled}
                    className={cn(
                        'group relative flex items-center h-8 outline-none',
                        isPrimary
                            ? 'bg-grey-550 hover:bg-grey-500 px-2 rounded-full'
                            : cn('rounded-md', responsive.trailingPadding),
                        active && 'z-20',
                        disabled &&
                            'opacity-50 cursor-not-allowed hover:bg-transparent',
                    )}
                >
                    <span
                        className={cn(
                            'flex items-center gap-1.5 transition-transform duration-150 ease-out',
                            active && onClear && responsive.hoverShift,
                        )}
                    >
                        <span className="relative inline-flex size-4 flex-shrink-0 items-center justify-center">
                            <IconComponent
                                className={cn(
                                    'size-4',
                                    active
                                        ? 'text-white-100'
                                        : 'text-grey-300 group-hover:text-grey-200',
                                )}
                            />
                            {active && (
                                <span
                                    className="pointer-events-none absolute bottom-0 right-0 w-[3px] h-[3px] rounded-full bg-white-100"
                                    aria-hidden="true"
                                />
                            )}
                        </span>
                        <span
                            className={cn(
                                'text-sm font-medium whitespace-nowrap',
                                responsive.label,
                                active
                                    ? 'text-grey-100'
                                    : 'text-grey-300 group-hover:text-grey-200',
                            )}
                        >
                            {label}
                        </span>
                    </span>
                    {active && onClear && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={handleClearClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleClearClick(
                                        e as unknown as React.MouseEvent,
                                    );
                                }
                            }}
                            className={cn(
                                'absolute right-2 size-4 flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity text-white-100 hover:text-grey-200 cursor-pointer outline-none focus:opacity-100',
                                responsive.closeIcon,
                            )}
                            aria-label="Clear filter"
                        >
                            <XIcon size={16} />
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                side="bottom"
                align={align}
                sideOffset={8}
                {...handleContentInteraction}
                className="min-w-[250px] z-50"
            >
                {title && <DropdownMenuLabel>{title}</DropdownMenuLabel>}
                <DropdownMenuGroup>{children}</DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
